import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { CheckCircle2, ArrowLeft, Clock, AlertTriangle, Calendar, User, Clock2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getPIPDetails } from '@/services/performanceService';

const StatusBadge = ({ status }) => {
    const config = {
        "Pending": "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
        "Draft": "bg-muted text-muted-foreground border-muted-foreground/20 dark:bg-muted/30 dark:text-muted-foreground dark:border-muted-foreground/30",
        "In Progress": "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800",
        "Completed": "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800",
        "Cancelled": "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",
    };
    return <Badge variant="outline" className={`text-[10px] ${config[status] || "bg-muted text-muted-foreground"}`}>{status}</Badge>;
};

const EmployeePIPView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const backTo = location.state?.returnTo || '/employee/reviews?tab=pips';
    const [pip, setPip] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadPIP();
    }, [id]);

    const loadPIP = async () => {
        try {
            setLoading(true);
            const pipResponse = await getPIPDetails(id);
            console.log("PIP Details : ", pipResponse.data);
            const pipData = pipResponse.data;
            setPip(pipData);
        } catch (error) {
            console.error("Error fetching PIP:", error);
            toast.error("Failed to load PIP details");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">Performance Improvement Plan</h1>
                    <p className="text-muted-foreground mt-1">Loading PIP details...</p>
                </div>
                <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">Loading...</CardContent>
                </Card>
            </div>
        );
    }

    if (!pip) {
        return (
            <div className="space-y-6">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">Performance Improvement Plan</h1>
                </div>
                <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                        <p className="text-lg">PIP record not found.</p>
                        <Button variant="outline" className="mt-4" onClick={() => navigate(backTo)}>
                            Back to Reviews
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const isCompleted = pip.status === 'Completed';
    const isCancelled = pip.status === 'Cancelled';
    const pipMilestones = pip.pip_milestones || [];
    const completedMilestones = pipMilestones.filter(m => m.completed === true).length;
    const totalMilestones = pipMilestones.length;
    const milestonePct = totalMilestones > 0 ? Math.round((completedMilestones / totalMilestones) * 100) : 0;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="mb-6">
                <Button
                    variant="ghost"
                    size="sm"
                    className="mb-4"
                    onClick={() => navigate(backTo)}
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Reviews
                </Button>

                <div className="mb-4">
                    {/* Status Messages */}

                    {isCancelled && (
                        <Card className="border-amber-200 bg-amber-50/80 dark:border-amber-900 dark:bg-amber-950/20">
                            <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                    <AlertCircle className="text-amber-600 h-5 w-5 sm:h-8 sm:w-8 mt-0.5 shrink-0" />
                                    <div>
                                        <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-300">PIP Cancelled</h4>
                                        <p className="text-xs text-amber-700/80 dark:text-amber-300/80 mt-1">
                                            This Performance Improvement Plan has been cancelled and is no longer active.
                                        </p>
                                        {pip.cancellation_reason && ( 
                                            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                                                <span className="leading-relaxed truncate">Reason : </span> {pip.cancellation_reason}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {isCompleted && (
                        <Card className="border-primary/20 dark:border-primary bg-primary/10 dark:bg-primary/10">
                            <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                                    <div>
                                        <h4 className="text-sm font-semibold text-primary">PIP Completed Successfully</h4>
                                        <p className="text-xs text-primary mt-1">
                                            This Performance Improvement Plan has been completed and finalized by your manager.
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {!isCompleted && !isCancelled && pip.status === 'Draft' && (
                        <Card className="border-primary/20 dark:border-primary bg-primary/10 dark:bg-primary/10">
                            <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                    <Clock className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                                    <div>
                                        <h4 className="text-sm font-semibold text-primary">PIP In Progress</h4>
                                        <p className="text-xs text-primary mt-1">
                                            Your manager is actively monitoring your progress. Complete your milestones as scheduled and reach out if you need support.
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                    <div className="flex-1 min-w-0">
                        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Performance Improvement Plan</h1>
                        <p className="text-sm sm:text-base text-muted-foreground mt-1">View your PIP details and progress.</p>
                    </div>
                    <Badge className={`w-[fit-content] text-sm py-2 shrink-0
                        ${pip.status === 'Draft' ? 'bg-muted text-muted-foreground border-muted-foreground/20 dark:bg-muted/30 dark:text-muted-foreground dark:border-muted-foreground/30' 
                            : pip.status === 'Completed' ? 'bg-primary text-primary-foreground border-primary dark:bg-primary dark:border-primary' 
                            : pip.status === 'Cancelled' ? 'border-amber-200 bg-amber-50/80 dark:border-amber-900 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300' : ''}
                        `}>
                        {pip.status === 'Draft' ? 'Pending' : pip.status}
                    </Badge>
                </div>
            </div>

            {/* PIP Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Employee Info */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <User className="h-5 w-5 text-primary" />
                            Employee Information
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            <div className="space-y-2 text-sm">
                                <div className='flex justify-between'>
                                    <span className="text-muted-foreground">Employee Name:</span>
                                    <span className="font-medium">{pip.employee_name || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Employee ID:</span>
                                    <span className="font-medium">{pip.employee_id?.employee_id || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Designation:</span>
                                    <span className="font-medium">{pip.employee_id?.designation || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Department:</span>
                                    <span className="font-medium">{pip.department || 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Timeline */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Calendar className="h-5 w-5 text-primary" />
                            PIP Timeline
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Start Date</span>
                                <span className="text-sm font-medium">
                                    {new Date(pip.pip_start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">End Date</span>
                                <span className="text-sm font-medium">
                                    {new Date(pip.pip_end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                            </div>
                            <Separator />
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Duration</span>
                                <span className="text-sm font-medium">
                                    {Math.ceil((new Date(pip.pip_end_date) - new Date(pip.pip_start_date)) / (1000 * 60 * 60 * 24))} days
                                </span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Progress */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Clock className="h-5 w-5 text-primary" />
                            Progress
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground">Milestone Completion</span>
                                    <span className="font-medium">{milestonePct}%</span>
                                </div>
                                <Progress value={milestonePct} className="h-2" />
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <span>{completedMilestones} of {totalMilestones} completed</span>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Reason for PIP */}
            {pip.pip_reason && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-primary" />
                            Reason for PIP
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="rounded-lg">
                            <p className="text-sm text-foreground whitespace-pre-wrap">{pip.pip_reason}</p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Milestones */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Clock className="h-5 w-5 text-primary" />
                        Review Milestones
                    </CardTitle>
                    <CardDescription>
                        Track the progress of your performance improvement milestones.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {pipMilestones.length === 0 ? (
                        <div className="py-8 text-center text-muted-foreground">
                            <p>No milestones defined for this PIP.</p>
                        </div>
                    ) : (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-x-auto thin-scrollbar" style={{ scrollbarWidth: 'thin' }}>
                            <div className="min-w-[800px]" style={{ scrollbarWidth: 'thin' }}>
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-b border-gray-200 dark:border-gray-700">
                                        <TableHead className="text-gray-700 dark:text-gray-300 w-12">#</TableHead>
                                        <TableHead className="text-gray-700 dark:text-gray-300">Milestone</TableHead>
                                        <TableHead className="text-gray-700 dark:text-gray-300">Target Date</TableHead>
                                        <TableHead className="text-gray-700 dark:text-gray-300">Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {pipMilestones.map((ms, idx) => {
                                        const isDone = ms.completed === true || ms.status === 'Completed';
                                        return (
                                            <TableRow key={ms.milestone_id || ms.id || idx} className="border-b border-gray-200 dark:border-gray-700">
                                                <TableCell className="text-gray-800 dark:text-gray-300">
                                                    <div className={`h-7 w-7 rounded-full flex items-center justify-center ${isDone ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                                                        {isDone ? <CheckCircle2 className="h-4 w-4" /> : <span className="text-xs font-semibold">{idx + 1}</span>}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="font-medium text-gray-900 dark:text-gray-200">
                                                        {ms.label}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-gray-800 dark:text-gray-300">
                                                    {new Date(ms.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={`text-[10px] ${isDone
                                                        ? 'bg-primary text-primary-foreground border-primary dark:bg-primary dark:border-primary'
                                                        : 'bg-muted text-muted-foreground border-muted-foreground/20 dark:bg-muted/30 dark:text-muted-foreground dark:border-muted-foreground/30'
                                                        }`}>
                                                        {isDone ? 'Completed' : 'Pending'}
                                                    </Badge>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

           
        </div>
    );
};

export default EmployeePIPView;
